import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={56} />
          <h1 className="mt-4 text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Law Agency 360</h1>
          <p className="text-sm text-slate-400 mt-1">Forgot your password?</p>
        </div>
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {submitted ? (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Check your inbox</h2>
              <p className="text-sm text-slate-400">
                If an account with that email exists, a password reset link has been sent. The link expires in 60 minutes.
              </p>
              <Link to="/auth?mode=signin" className="inline-flex items-center gap-2 mt-6 text-sm text-violet-300 hover:text-violet-200">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <p className="text-sm text-slate-300">
                Enter the email associated with your account and we'll send you a password reset link.
              </p>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Email address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-800/60 border border-white/10 text-white rounded-xl px-4 py-3 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition"
                  placeholder="you@firm.com"
                  data-testid="forgot-password-email"
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-password-error">{error}</p>}
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="forgot-password-submit"
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
              <Link
                to="/auth?mode=signin"
                className="block text-center text-sm text-slate-400 hover:text-violet-300"
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
